/**
 * The Statistics page's reading of what the Worker answered — pure, so the page
 * only draws. The figures and the findings are the desktop app's own
 * (`@airlock/contracts/usage`): a member reads here the same sentences their
 * Airlock shows them, on all of their machines at once, and an administrator
 * reads them for the whole organization, plus where each member stands.
 */
import { analyse, pairLabel } from '@airlock/contracts/usage/analysis';
import { dayOf, daysBetween, sumDays, type UsageDayRow } from '@airlock/contracts/usage/day';
import { figureTables, keyFigures, rangeWords, type FigureTables, type KeyFigure } from '@airlock/contracts/usage/figures';
import type { Finding } from '@airlock/contracts/usage/findings';
import { memberFindings, memberSpread, type MemberRow, type MemberSpread } from '@airlock/contracts/usage/members';
import { buildReport, rangeOf, type OrgUsageAnswer, type UsageReport } from '@airlock/contracts/usage/report';

export interface UsageScreen {
  report: UsageReport;
  figures: KeyFigure[];
  tables: FigureTables;
  words: { range: string; against: string };
  /** Only today has ever been sent: no trend, no comparison. */
  fresh: boolean;
  act: Finding[];
  know: Finding[];
  /** An administrator reading everybody: one row per member sending, by e-mail. */
  members: MemberSpread | null;
  memberFindings: Finding[];
}

/** The window the page asks the Worker for: this period and the one before it. */
export function windowOf(days: number, today = dayOf(Date.now())): { from: string; to: string; today: string } {
  const range = rangeOf(today, days);
  return { from: range.prevFrom, to: range.to, today };
}

/** Every member's days added together, day by day — the organization as one. */
export function combined(answer: OrgUsageAnswer, only: string | null): UsageDayRow[] {
  const byDay = new Map<string, UsageDayRow['data'][]>();
  for (const member of answer.members) {
    if (only && member.email !== only) continue;
    for (const row of member.days) byDay.set(row.day, [...(byDay.get(row.day) ?? []), row.data]);
  }
  return [...byDay.entries()].map(([day, data]) => ({ day, data: sumDays(data) }));
}

export function screenOf(answer: OrgUsageAnswer, days: number, today: string, only: string | null): UsageScreen {
  const rows = combined(answer, only);
  const report = buildReport(rows, today, days, 'account');
  const fresh = rows.length > 0 && rows.every((row) => row.day === today);
  const all = daysBetween(report.from, report.to);
  let members: MemberSpread | null = null;
  let spread: Finding[] = [];
  if (answer.everyone && !only) {
    const list: MemberRow[] = answer.members
      .map((member) => {
        const analysis = analyse(member.days, report.from, report.to, all);
        const top = [...analysis.pairs].sort((a, b) => b.turns - a.turns)[0];
        return {
          member: member.email,
          machines: member.machines,
          lastSent: member.lastSent,
          mostly: top ? pairLabel(top.model, top.effort) : null,
          ...analysis.totals
        };
      })
      .sort((a, b) => a.member.localeCompare(b.member));
    members = memberSpread(list);
    spread = memberFindings(list);
  }
  return {
    report,
    figures: keyFigures(report, `the previous ${days} days`),
    tables: figureTables(report, today),
    words: rangeWords(report, fresh),
    fresh,
    act: report.findings.filter((f) => f.group === 'act'),
    know: report.findings.filter((f) => f.group === 'know'),
    members,
    memberFindings: spread
  };
}

/** `today, 09:12`, `yesterday, 18:03`, `3 Oct, 16:40` — when a member's last day arrived. */
export function lastSentWords(iso: string | null, now = new Date()): string {
  if (!iso) return '—';
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (at.toDateString() === now.toDateString()) return `today, ${time}`;
  if (at.toDateString() === yesterday.toDateString()) return `yesterday, ${time}`;
  return `${at.getDate()} ${at.toLocaleString('en-GB', { month: 'short' })}, ${time}`;
}
